import { useNavigate } from 'react-router-dom';

import { BadgeGroup, College, ROILineChart } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SOLUTION_SESSION_IDS } from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { InvestmentCard } from '../atoms/InvestmentCard';

const RETURN_ON_INVESTMENT_STRINGS = appStrings.modules.college.returnOnInvestment;

const AVERAGE_ROI = 145;
const AVERAGE_DATA = [
  0, -61437, -124677, -189772, -256776, -202082, -146684, -90775, -34529, 27486, 92100, 158761,
  226839, 295644, 364449,
];

type ReturnOnInvestmentProps = Readonly<{
  college: College;
}>;

export const ReturnOnInvestment = ({ college }: ReturnOnInvestmentProps) => {
  const navigate = useNavigate();

  return (
    <Box sx={containerSx}>
      {/* TITLE */}
      <Box sx={titleContainerSx}>
        <Box>
          <Typography component="div" variant="display-sm-medium">
            {RETURN_ON_INVESTMENT_STRINGS.header.title}
          </Typography>

          <Typography variant="text-sm-regular">
            {RETURN_ON_INVESTMENT_STRINGS.header.subtitle}
          </Typography>
        </Box>

        <BadgeGroup
          badgeName={RETURN_ON_INVESTMENT_STRINGS.header.askLubavTitle}
          title={RETURN_ON_INVESTMENT_STRINGS.header.askLubavDescription}
          isOpen={false}
          setIsOpen={() => {
            navigate(
              APP_ROUTE.SOLUTION_SESSION.replace(
                ':id?',
                SOLUTION_SESSION_IDS.LOWER_COST_OF_COLLEGE,
              ),
            );
          }}
          children={<></>}
        />
      </Box>

      {/* INVESTMENT CARDS */}
      {(college.est_total_investment || college.est_10yr_earning) && (
        <Box sx={cardsContainerSx}>
          {college.est_total_investment && (
            <InvestmentCard
              title={RETURN_ON_INVESTMENT_STRINGS.cards.estimatedInvestment.title}
              tooltipText={RETURN_ON_INVESTMENT_STRINGS.cards.estimatedInvestment.tooltip}
              value={college.est_total_investment}
            />
          )}

          {college.est_10yr_earning && (
            <InvestmentCard
              title={RETURN_ON_INVESTMENT_STRINGS.cards.estimated10YearReturn.title}
              tooltipText={RETURN_ON_INVESTMENT_STRINGS.cards.estimated10YearReturn.tooltip}
              value={college.est_10yr_earning}
            />
          )}
        </Box>
      )}

      {/* ROI LINE CHART */}
      <ROILineChart
        averageData={AVERAGE_DATA}
        actualData={[
          0,
          college.return_yr1 || 0,
          college.return_yr2 || 0,
          college.return_yr3 || 0,
          college.return_yr4 || 0,
          college.return_yr5 || 0,
          college.return_yr6 || 0,
          college.return_yr7 || 0,
          college.return_yr8 || 0,
          college.return_yr9 || 0,
          college.return_yr10 || 0,
          college.return_yr11 || 0,
          college.return_yr12 || 0,
          college.return_yr13 || 0,
          college.return_yr14 || 0,
        ]}
        title={RETURN_ON_INVESTMENT_STRINGS.header.title}
        total_roi={college.est_14yr_roi || 0}
        average_roi={AVERAGE_ROI}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  mb: backrs.spacing['spacing-6xl'],
  marginX: backrs.spacing['spacing-4xl'],
  '@media (max-height: 768px)': {
    mx: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 414px)': {
    mx: backrs.spacing['spacing-2xl'],
  },
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-2xl'],
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-2xl'],
});

const cardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  mt: backrs.spacing['spacing-2xl'],
  flexDirection: { xs: 'column', sm: 'row' },
  gap: backrs.spacing['spacing-3xl'],
  mb: backrs.spacing['spacing-2xl'],
});
